import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, X, FileText, AlertTriangle, ExternalLink } from 'lucide-react';
import {
	getStatutorySummary,
	getStatutoryClose,
	getStatutoryMatrix,
	getStatutoryFindings,
	getStatutoryFinding,
	updateStatutoryFindings,
	getStatutoryEvidenceFile,
	inr,
	inrFull,
} from '../data';
import { StatutoryCloseView } from './StatutoryClose';
import { StatutoryMatrixView } from './StatutoryMatrix';
import type {
	StatutorySummary,
	StatutoryClose,
	StatutoryMatrix,
	StatutoryFinding,
	StatutoryFindingDetail,
	StatutoryStatus,
} from '../types';

/**
 * Statutory reconciliation — PF, PF EDLI/Admin, ESIC, PT.
 *
 * The screen answers three questions and nothing else: what is wrong, who has to move, and how
 * do we know. Everything on it comes from /statutory-recon; there is no client-side arithmetic
 * over money, because a number computed twice is a number that disagrees with itself.
 *
 * NOT EVERY LEG HAS DATA. The banner at the top says which ones do, counted by the API in
 * `legs_built`/`legs_not_built`. Without it this page reads as a finished reconciliation.
 */

const STATUS_LABEL: Record<StatutoryStatus, string> = {
	needs_review: 'Needs review',
	in_progress: 'In progress',
	reviewed_ok: 'Signed off',
	wont_fix: "Won't fix",
	blocked: 'Blocked',
};

const STATUS_STYLE: Record<StatutoryStatus, string> = {
	needs_review: 'bg-amber-50 text-amber-700 border-amber-200',
	in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
	reviewed_ok: 'bg-green-50 text-green-700 border-green-200',
	wont_fix: 'bg-gray-100 text-gray-600 border-gray-200',
	blocked: 'bg-red-50 text-red-700 border-red-200',
};

/** A..F. The register's own grouping — money first, because that is what gets worked first. */
const GROUPS = [
	{ id: '', label: 'All' },
	{ id: 'A', label: 'Money' },
	{ id: 'B', label: 'Evidence' },
	{ id: 'C', label: 'Clients' },
	{ id: 'D', label: 'Structural' },
	{ id: 'E', label: 'Security' },
	{ id: 'F', label: 'Identity' },
];

const OWNER_LABEL: Record<string, string> = {
	accounts: 'Accounts',
	engineering: 'Engineering',
	swati_vyapar: 'Swati → Vyapar',
	client_facing: 'Client-facing',
	founder: 'Founder',
	UNASSIGNED: 'Unassigned',
};

/** Leg names as an accountant would say them, not as the API keys them. */
const LEG_LABEL: Record<string, string> = {
	zoho: 'the Zoho ledger',
	challan: 'the challans',
	return: 'the filed returns',
	bank: 'the bank statement',
	salary_sheet: 'the salary sheet',
};

const ownerName = (o: string) => OWNER_LABEL[o] ?? o;

/**
 * Open an evidence file.
 *
 * Fetched through the axios service, NOT linked with a plain href. The document proxy is
 * auth-gated and a new tab carries no Authorization header, so a direct link 401s — which looks
 * like "the evidence is missing" when it is only unauthenticated.
 */
const openEvidenceFile = async (key: string) => {
	const url = await getStatutoryEvidenceFile(key);
	window.open(url, '_blank', 'noopener');
};

export const StatutoryRecon: React.FC = () => {
	const [summary, setSummary] = useState<StatutorySummary | null>(null);
	const [close, setClose] = useState<StatutoryClose | null>(null);
	const [matrix, setMatrix] = useState<StatutoryMatrix | null>(null);
	// Which of the two top views is showing. The close view is the balance question — is the
	// control account right. The matrix is the evidence question — do the challan, the return, the
	// bank and the books all say the same thing. Different questions, so they are not one screen.
	const [view, setView] = useState<'close' | 'matrix'>('close');
	const [rows, setRows] = useState<StatutoryFinding[]>([]);
	const [loading, setLoading] = useState(true);
	const [group, setGroup] = useState('');
	const [owner, setOwner] = useState('');
	const [openOnly, setOpenOnly] = useState(true);
	const [detail, setDetail] = useState<StatutoryFindingDetail | null>(null);
	const [detailLoading, setDetailLoading] = useState(false);
	const [saving, setSaving] = useState<number | null>(null);
	const [toast, setToast] = useState<{ text: string; warn: boolean } | null>(null);

	const load = async () => {
		setLoading(true);
		try {
			const [s, c, mx, f] = await Promise.all([
				getStatutorySummary(),
				getStatutoryClose(),
				getStatutoryMatrix(),
				getStatutoryFindings({ group: group || undefined, owner: owner || undefined }),
			]);
			setSummary(s);
			setClose(c);
			setMatrix(mx);
			setRows(f.rows);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		void load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [group, owner]);

	// Filtered here rather than server-side: it is a two-value toggle over rows already loaded,
	// and a round trip to hide six signed-off rows is a round trip nobody needs.
	const visible = useMemo(
		() => (openOnly ? rows.filter(r => r.status !== 'reviewed_ok' && r.status !== 'wont_fix') : rows),
		[rows, openOnly]
	);

	const openDetail = async (id: number) => {
		setDetailLoading(true);
		try {
			setDetail(await getStatutoryFinding(id));
		} finally {
			setDetailLoading(false);
		}
	};

	/**
	 * Sign-off. A 200 does not mean it was written — `refused` and `not_found` have to be read,
	 * and a refusal is shown with the server's own sentence rather than a shorter invention.
	 * Refetches instead of patching state: the row, the owner counts and the header all move.
	 */
	const setStatus = async (id: number, status: StatutoryStatus) => {
		setSaving(id);
		try {
			const res = await updateStatutoryFindings([id], status);
			if (res.refused.length) {
				// The server's sentence, verbatim except for the two status tokens — an accountant
				// reading "reviewed_ok" has to translate it, and the rest of this screen already
				// calls them "Signed off" and "Won't fix". Rewording the sentence itself would be
				// inventing a shorter version, which is what the API comment warns against.
				setToast({
					text: res.refused[0].reason
						.replace(/reviewed_ok/g, `“${STATUS_LABEL.reviewed_ok}”`)
						.replace(/wont_fix/g, `“${STATUS_LABEL.wont_fix}”`),
					warn: true,
				});
			} else if (res.updated.length) {
				const u = res.updated[0];
				setToast({
					text: `${u.code} set to ${STATUS_LABEL[u.status]}. ${res.open_now.findings} findings still open, ${inrFull(res.open_now.liable_rs)} liable.`,
					warn: false,
				});
			} else {
				setToast({ text: 'Nothing was updated.', warn: true });
			}
			await load();
			if (detail?.id === id) await openDetail(id);
		} finally {
			setSaving(null);
		}
	};

	if (loading && !summary) {
		return (
			<div className='flex items-center justify-center py-20 text-gray-500'>
				<Loader2 className='w-5 h-5 animate-spin mr-2' /> Loading statutory findings…
			</div>
		);
	}

	const owners = summary?.findings.by_owner ?? [];
	// Summed from the API's per-head counts. The only arithmetic on this screen, and it is a count
	// of months rather than money — money is never recomputed client-side.
	// Straight from the API. This used to sum `challan_vs_zoho`, which reads the v1 view — challan
	// against Zoho only, blind to the return and bank legs. It called September ESIC "no challan"
	// when the return declares 61,322 and ICICI paid exactly 61,322, and so reported 24 of 36 when
	// 31 were confirmed. A headline that understates by seven months is one people plan around.
	const confirmed = summary?.bank_confirmed;

	return (
		<div className='space-y-5'>
			{/* The honesty banner. Straight from the API — never hardcoded, so it disappears by
			    itself the day the other three legs load. */}
			{!!summary?.legs_not_built?.length && (
				<div className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3'>
					<AlertTriangle className='w-4 h-4 text-amber-600 mt-0.5 shrink-0' />
					<div className='text-sm text-amber-900'>
						{/* Both halves counted from the API. "of 4" was a literal until a fifth leg — the
						    bank statement — landed on 4-Sep-2026, at which point the banner was quietly
						    wrong. A hardcoded denominator is the same bug as a hardcoded list. */}
						<span className='font-medium'>
							{summary.legs_built?.length ?? 0} of{' '}
							{(summary.legs_built?.length ?? 0) + summary.legs_not_built.length} legs loaded
						</span>{' '}
						— {(summary.legs_built ?? ['zoho']).map(l => LEG_LABEL[l] ?? l).join(', ')}. Still
						missing: {summary.legs_not_built.map(l => LEG_LABEL[l] ?? l).join(' and ')}, so
						contributions have not been checked against what was actually paid to staff.
					</div>
				</div>
			)}

			{/* Two views, one switch. Kept as buttons rather than a second tab strip because this
			    page already sits inside the Reconciliation tab bar and a nested strip of the same
			    shape reads as the same level of navigation. */}
			<div className='inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50'>
				{(
					[
						['close', 'Control account'],
						['matrix', 'Challan · return · bank · Zoho'],
					] as const
				).map(([k, label]) => (
					<button
						key={k}
						onClick={() => setView(k)}
						className={`px-3 py-1.5 text-sm rounded-md transition ${
							view === k ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-800'
						}`}>
						{label}
					</button>
				))}
			</div>

			{view === 'close'
				? close && <StatutoryCloseView close={close} onFinding={openDetail} />
				: matrix && <StatutoryMatrixView matrix={matrix} />}

			<div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
				<Tile
					label='Open findings'
					value={String(summary?.findings.open ?? 0)}
					sub={`${summary?.findings.total ?? 0} in the register`}
				/>
				<Tile
					label='Still liable'
					value={inr(summary?.findings.net_liable_rs ?? summary?.findings.liable_rs ?? 0)}
					sub={
						summary?.findings.net_liable_rs != null &&
						summary.findings.net_liable_rs !== summary.findings.liable_rs
							? `net of duplicates · ${inr(summary.findings.liable_rs)} before netting`
							: 'reported minus explained'
					}
				/>
				<Tile
					label='Months confirmed against the bank'
					value={confirmed?.months ? `${confirmed.confirmed}/${confirmed.months}` : '—'}
					sub='money provably left the bank · PF · ESIC · PT, FY 2025-26'
				/>
			</div>

			{/* Who has to move next. The register's whole point — a finding without an owner is a
			    note, and notes do not get done. */}
			<div>
				<h3 className='text-sm font-medium text-gray-700 mb-2'>
					Open items <span className='font-normal text-gray-400'>— the exception queue behind the grid</span>
				</h3>
				<div className='text-xs font-medium text-gray-500 mb-2'>Who moves next</div>
				<div className='flex flex-wrap gap-2'>
					<button
						onClick={() => setOwner('')}
						className={`px-3 py-1.5 text-xs rounded-full border transition ${
							owner === '' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
						}`}>
						Everyone
					</button>
					{owners.map(o => (
						<button
							key={o.action_owner}
							onClick={() => setOwner(owner === o.action_owner ? '' : o.action_owner)}
							className={`px-3 py-1.5 text-xs rounded-full border transition ${
								owner === o.action_owner
									? 'bg-gray-900 text-white border-gray-900'
									: 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
							}`}>
							{ownerName(o.action_owner)} · {o.open}
							{o.liable_rs > 0 && (
								<span className={owner === o.action_owner ? 'text-gray-300' : 'text-gray-400'}> · {inr(o.liable_rs)}</span>
							)}
						</button>
					))}
				</div>
			</div>

			<div className='flex flex-wrap items-center gap-1 border-b border-gray-200 pb-2'>
				{GROUPS.map(g => (
					<button
						key={g.id}
						onClick={() => setGroup(g.id)}
						className={`px-3 py-1.5 text-sm rounded-md transition ${
							group === g.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-500 hover:text-gray-700'
						}`}>
						{g.label}
					</button>
				))}
				<label className='ml-auto flex items-center gap-2 text-xs text-gray-600 cursor-pointer'>
					<input type='checkbox' checked={openOnly} onChange={e => setOpenOnly(e.target.checked)} />
					Open only
				</label>
			</div>

			<div className='overflow-x-auto border border-gray-200 rounded-lg'>
				<table className='w-full text-sm'>
					<thead className='bg-gray-50 text-gray-500'>
						<tr>
							<th className='text-left px-3 py-2 font-medium w-14'>Code</th>
							<th className='text-left px-3 py-2 font-medium'>Finding</th>
							<th className='text-left px-3 py-2 font-medium w-32'>Owner</th>
							<th className='text-right px-3 py-2 font-medium w-28'>Liable</th>
							<th className='text-center px-3 py-2 font-medium w-20'>Evidence</th>
							<th className='text-left px-3 py-2 font-medium w-40'>Status</th>
						</tr>
					</thead>
					<tbody className='divide-y divide-gray-100'>
						{visible.map(r => (
							<tr key={r.id} className='hover:bg-gray-50 align-top'>
								<td className='px-3 py-3'>
									<button
										onClick={() => openDetail(r.id)}
										className='font-mono text-xs font-semibold text-blue-600 hover:underline'>
										{r.code}
									</button>
								</td>
								<td className='px-3 py-3'>
									<button onClick={() => openDetail(r.id)} className='text-left'>
										<div className='text-gray-900 line-clamp-2'>{r.plain_summary}</div>
										<div className='text-xs text-gray-500 mt-1'>→ {r.action_required}</div>
									</button>
								</td>
								<td className='px-3 py-3 text-gray-600 text-xs'>{ownerName(r.action_owner)}</td>
								<td className='px-3 py-3 text-right font-medium tabular-nums'>
									{r.liable_rs ? inrFull(r.liable_rs) : <span className='text-gray-300'>—</span>}
								</td>
								<td className='px-3 py-3 text-center'>
									{r.evidence_lines > 0 ? (
										<button
											onClick={() => openDetail(r.id)}
											className='inline-flex items-center gap-1 text-xs text-gray-600 hover:text-blue-600'>
											<FileText className='w-3 h-3' /> {r.evidence_lines}
										</button>
									) : (
										<span className='text-xs text-gray-300'>none</span>
									)}
								</td>
								<td className='px-3 py-3'>
									<select
										value={r.status}
										disabled={saving === r.id}
										onChange={e => setStatus(r.id, e.target.value as StatutoryStatus)}
										className={`text-xs rounded-md border px-2 py-1 w-full ${STATUS_STYLE[r.status]}`}>
										{(Object.keys(STATUS_LABEL) as StatutoryStatus[]).map(s => (
											<option key={s} value={s}>
												{STATUS_LABEL[s]}
											</option>
										))}
									</select>
								</td>
							</tr>
						))}
						{!visible.length && (
							<tr>
								<td colSpan={6} className='px-3 py-10 text-center text-gray-400 text-sm'>
									No findings match this filter.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{toast && (
				<div
					className={`fixed bottom-6 right-6 max-w-md rounded-lg border px-4 py-3 text-sm shadow-lg z-50 ${
						toast.warn ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-white border-gray-200 text-gray-800'
					}`}>
					<div className='flex items-start gap-2'>
						<span className='flex-1'>{toast.text}</span>
						<button onClick={() => setToast(null)} className='text-gray-400 hover:text-gray-600'>
							<X className='w-4 h-4' />
						</button>
					</div>
				</div>
			)}

			{(detail || detailLoading) && (
				<EvidenceDrawer
					detail={detail}
					loading={detailLoading}
					onClose={() => setDetail(null)}
					onStatus={setStatus}
					saving={saving}
				/>
			)}
		</div>
	);
};

const Tile: React.FC<{ label: string; value: string; sub: string }> = ({ label, value, sub }) => (
	<div className='rounded-lg border border-gray-200 bg-white px-4 py-3'>
		<div className='text-xs text-gray-500'>{label}</div>
		<div className='text-2xl font-semibold text-gray-900 mt-0.5'>{value}</div>
		<div className='text-xs text-gray-400 mt-0.5'>{sub}</div>
	</div>
);

/**
 * The working behind one finding.
 *
 * Each evidence line renders as what it actually is — a Zoho ledger row shows its debit and
 * credit, an email attachment shows the subject it arrived under and opens the real file. An
 * attachment with no `storage_key` was never mirrored, and says so instead of offering a dead
 * link (that is finding B1a, not a UI bug).
 */
const EvidenceDrawer: React.FC<{
	detail: StatutoryFindingDetail | null;
	loading: boolean;
	onClose: () => void;
	onStatus: (id: number, s: StatutoryStatus) => void;
	saving: number | null;
}> = ({ detail, loading, onClose, onStatus, saving }) => (
	<div className='fixed inset-0 z-40 flex justify-end'>
		<div className='absolute inset-0 bg-black/20' onClick={onClose} />
		<div className='relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl'>
			{loading || !detail ? (
				<div className='flex items-center justify-center h-full text-gray-500'>
					<Loader2 className='w-5 h-5 animate-spin mr-2' /> Loading…
				</div>
			) : (
				<>
					<div className='sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start gap-3'>
						<div className='flex-1'>
							<div className='flex items-center gap-2'>
								<span className='font-mono text-xs font-semibold text-gray-500'>{detail.code}</span>
								<span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[detail.status]}`}>
									{STATUS_LABEL[detail.status]}
								</span>
								<span className='text-xs text-gray-400'>{ownerName(detail.action_owner)}</span>
							</div>
							<h3 className='text-base font-medium text-gray-900 mt-2'>{detail.plain_summary}</h3>
						</div>
						<button onClick={onClose} className='text-gray-400 hover:text-gray-600'>
							<X className='w-5 h-5' />
						</button>
					</div>

					<div className='px-6 py-4 space-y-5'>
						<div className='rounded-lg bg-blue-50 border border-blue-100 px-4 py-3'>
							<div className='text-xs font-medium text-blue-900 mb-1'>What has to happen next</div>
							<div className='text-sm text-blue-900'>{detail.action_required}</div>
						</div>

						{detail.reported_rs !== null && (
							<div className='grid grid-cols-3 gap-3 text-center'>
								<Figure label='Reported' value={inrFull(detail.reported_rs)} />
								<Figure label='Explained' value={inrFull(detail.explained_rs ?? 0)} />
								<Figure label='Still liable' value={inrFull(detail.liable_rs ?? 0)} strong />
							</div>
						)}

						<div>
							<div className='text-xs font-medium text-gray-500 mb-2'>
								How we cross-checked it — {detail.evidence.length} evidence line
								{detail.evidence.length === 1 ? '' : 's'}
							</div>
							{!detail.evidence.length && (
								<div className='text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg px-4 py-6 text-center'>
									No evidence line yet. The finding stands on the write-up in {detail.source_doc ?? 'the register'}.
								</div>
							)}
							<div className='space-y-2'>
								{detail.evidence.map(e => (
									<div key={e.id} className='border border-gray-200 rounded-lg px-3 py-2.5 text-sm'>
										<div className='text-gray-800'>{e.summary}</div>

										{e.zoho_ledger_row && (
											<div className='mt-2 grid grid-cols-4 gap-2 text-xs bg-gray-50 rounded px-2 py-1.5'>
												<span className='text-gray-500'>{e.zoho_ledger_row.txn_date}</span>
												<span className='text-gray-600'>{e.zoho_ledger_row.txn_type}</span>
												<span className='text-right tabular-nums'>
													{e.zoho_ledger_row.debit > 0 ? `Dr ${inrFull(e.zoho_ledger_row.debit)}` : ''}
												</span>
												<span className='text-right tabular-nums'>
													{e.zoho_ledger_row.credit > 0 ? `Cr ${inrFull(e.zoho_ledger_row.credit)}` : ''}
												</span>
											</div>
										)}

										{e.email_attachment && (
											<div className='mt-2 text-xs bg-gray-50 rounded px-2 py-1.5'>
												<div className='text-gray-600'>
													{e.email_attachment.date_sent} · {e.email_attachment.account_email}
												</div>
												<div className='text-gray-500 italic mt-0.5'>
													“{e.email_attachment.subject}”
												</div>
												{e.email_attachment.storage_key ? (
													<button
														onClick={() => openEvidenceFile(e.email_attachment!.storage_key!)}
														className='inline-flex items-center gap-1 text-blue-600 hover:underline mt-1'>
														<ExternalLink className='w-3 h-3' /> {e.email_attachment.filename}
													</button>
												) : (
													<div className='text-amber-700 mt-1'>
														{e.email_attachment.filename} — not mirrored, cannot open yet
													</div>
												)}
											</div>
										)}

										{e.email_message && (
											<div className='mt-2 text-xs bg-gray-50 rounded px-2 py-1.5'>
												<div className='text-gray-600'>
													{e.email_message.date_sent} · {e.email_message.from_addr} →{' '}
													{e.email_message.to_addrs}
												</div>
												<div className='text-gray-500 italic mt-0.5'>“{e.email_message.subject}”</div>
												<div className='text-gray-500 mt-1'>
													{e.email_message.attachment_count} attachment
													{e.email_message.attachment_count === 1 ? '' : 's'}, none mirrored:{' '}
													{e.email_message.filenames.slice(0, 3).join(', ')}
													{e.email_message.filenames.length > 3 ? '…' : ''}
												</div>
											</div>
										)}
									</div>
								))}
							</div>
						</div>

						<div>
							<div className='text-xs font-medium text-gray-500 mb-2'>Set status</div>
							<select
								value={detail.status}
								disabled={saving === detail.id}
								onChange={e => onStatus(detail.id, e.target.value as StatutoryStatus)}
								className={`text-sm rounded-md border px-3 py-2 ${STATUS_STYLE[detail.status]}`}>
								{(Object.keys(STATUS_LABEL) as StatutoryStatus[]).map(s => (
									<option key={s} value={s}>
										{STATUS_LABEL[s]}
									</option>
								))}
							</select>
							<div className='text-xs text-gray-400 mt-2'>
								Signing off is refused while money is still unexplained. Use “Won’t fix” to write one
								off deliberately.
							</div>
						</div>

						{detail.source_doc && (
							<div className='text-xs text-gray-400 border-t border-gray-100 pt-3'>
								Source: {detail.source_doc}
							</div>
						)}
					</div>
				</>
			)}
		</div>
	</div>
);

const Figure: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
	<div className={`rounded-lg border px-3 py-2 ${strong ? 'border-amber-200 bg-amber-50' : 'border-gray-200'}`}>
		<div className='text-xs text-gray-500'>{label}</div>
		<div className={`text-sm font-semibold tabular-nums ${strong ? 'text-amber-900' : 'text-gray-900'}`}>{value}</div>
	</div>
);

export default StatutoryRecon;
